// ============================================
// BLOG COLLECTION PAGE SCHEMA
// Blog ana sayfası için CollectionPage Schema
// ============================================

import { useEffect } from 'react';
import { BlogMetadata } from '@/data/blog/types';

interface BlogCollectionSchemaProps {
  posts: BlogMetadata[];
  category?: string;
}

export const BlogCollectionSchema = ({ posts, category }: BlogCollectionSchemaProps) => {
  useEffect(() => {
    const pageTitle = category && category !== 'all' 
      ? `${category} - Google Ads Blog` 
      : 'Google Ads Blog - Uzman İçgörüleri';
    
    const pageDescription = category && category !== 'all'
      ? `${category} kategorisindeki Google Ads yazıları. 18 yıllık deneyimle hazırlanmış profesyonel içerikler.`
      : '18 yıllık deneyimimle Google Ads stratejileri, ipuçları ve güncellemeler. Bütçe optimizasyonu, sahte tıklama koruması ve daha fazlası.';

    // CollectionPage Schema
    const collectionSchema = {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      '@id': category && category !== 'all'
        ? `https://www.reklam-uzmani.com.tr/blog?category=${encodeURIComponent(category)}#collection`
        : 'https://www.reklam-uzmani.com.tr/blog#collection',
      name: pageTitle,
      description: pageDescription,
      url: category && category !== 'all'
        ? `https://www.reklam-uzmani.com.tr/blog?category=${encodeURIComponent(category)}`
        : 'https://www.reklam-uzmani.com.tr/blog',
      inLanguage: 'tr-TR',
      isPartOf: {
        '@type': 'WebSite',
        '@id': 'https://www.reklam-uzmani.com.tr#website',
        name: 'Dijital Mustafa - Google Ads Uzmanı',
        url: 'https://www.reklam-uzmani.com.tr'
      },
      breadcrumb: {
        '@type': 'BreadcrumbList',
        '@id': 'https://www.reklam-uzmani.com.tr/blog#breadcrumb',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: 'Ana Sayfa',
            item: 'https://www.reklam-uzmani.com.tr'
          },
          {
            '@type': 'ListItem',
            position: 2,
            name: 'Blog',
            item: 'https://www.reklam-uzmani.com.tr/blog'
          }
        ]
      },
      about: {
        '@type': 'Thing',
        name: 'Google Ads',
        description: 'Google Ads reklam platformu stratejileri ve optimizasyon teknikleri'
      },
      author: {
        '@type': 'Person',
        '@id': 'https://www.reklam-uzmani.com.tr#mustafakaya',
        name: 'Dijital Mustafa',
        url: 'https://www.reklam-uzmani.com.tr/hakkimda',
        jobTitle: 'Google Ads Uzmanı - 18 Yıl Deneyim',
        image: '/images/mustafa-kaya-profil.png'
      },
      publisher: {
        '@type': 'Organization',
        '@id': 'https://www.reklam-uzmani.com.tr#organization',
        name: 'Dijital Mustafa - Google Ads Uzmanı',
        url: 'https://www.reklam-uzmani.com.tr',
        logo: {
          '@type': 'ImageObject',
          url: 'https://www.reklam-uzmani.com.tr/images/logo-premium-minimalist.png',
          width: 600,
          height: 60
        },
        contactPoint: {
          '@type': 'ContactPoint',
          telephone: '+90-535-838-6297',
          contactType: 'Customer Service',
          areaServed: 'TR',
          availableLanguage: 'Turkish'
        }
      },
      hasPart: posts.slice(0, 20).map((post, index) => ({
        '@type': 'BlogPosting',
        '@id': `https://www.reklam-uzmani.com.tr/blog/${post.slug}`,
        position: index + 1,
        headline: post.seoTitle || post.title,
        description: post.metaDescription || post.excerpt,
        image: post.coverImage,
        datePublished: post.publishedAt,
        dateModified: post.publishedAt,
        author: {
          '@type': 'Person',
          name: post.author.name,
          jobTitle: post.author.title
        },
        keywords: post.tags.join(', '),
        articleSection: post.category,
        url: `https://www.reklam-uzmani.com.tr/blog/${post.slug}`,
        mainEntityOfPage: `https://www.reklam-uzmani.com.tr/blog/${post.slug}`
      })),
      numberOfItems: posts.length
    };

    // WebPage Schema (ek olarak)
    const webPageSchema = {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      '@id': 'https://www.reklam-uzmani.com.tr/blog#webpage',
      url: 'https://www.reklam-uzmani.com.tr/blog',
      name: pageTitle,
      description: pageDescription,
      inLanguage: 'tr-TR',
      isPartOf: {
        '@type': 'WebSite',
        '@id': 'https://www.reklam-uzmani.com.tr#website'
      },
      primaryImageOfPage: {
        '@type': 'ImageObject',
        url: posts[0]?.coverImage || '/images/logo-premium-minimalist.png'
      },
      datePublished: posts[0]?.publishedAt,
      dateModified: posts[0]?.publishedAt
    };

    const schemas = [collectionSchema, webPageSchema];

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(schemas);
    script.id = 'blog-collection-schema';
    
    // Mevcut script varsa kaldır
    const existingScript = document.getElementById('blog-collection-schema');
    if (existingScript) {
      existingScript.remove();
    }
    
    document.head.appendChild(script);

    return () => {
      const scriptToRemove = document.getElementById('blog-collection-schema');
      if (scriptToRemove) {
        scriptToRemove.remove();
      }
    };
  }, [posts, category]);

  return null;
};

export default BlogCollectionSchema;
